import type {
  CharacterGender,
  CharacterProfile,
  ProfessionId,
  RaceId,
  RaceIndices,
} from "../../domain/types";

export interface RaceOption {
  id: RaceId;
  label: string;
  description: string;
  indices: RaceIndices | null;
  locked?: boolean;
  image: string;
}

export const RACES: RaceOption[] = [
  {
    id: "human",
    label: "人类",
    description:
      "大陆各地最常见的居民。适应面广，各项体能以 100 作为本作的比较基准。",
    indices: { health: 100, stamina: 100, dodge: 100, attack: 100 },
    image: "/assets/creation-v2/race-human",
  },
  {
    id: "felyne",
    label: "艾露",
    description:
      "灵巧而精力充沛的兽人族群，擅长支援、采集与敏捷行动，但正面力量较弱。",
    indices: { health: 70, stamina: 180, dodge: 150, attack: 60 },
    image: "/assets/creation-v2/race-felyne-v2.webp",
  },
  {
    id: "wyverian",
    label: "龙人",
    description:
      "长寿、敏锐并重视传统与知识的族群；体能与人类接近，行动和耐力略有优势。",
    indices: { health: 90, stamina: 110, dodge: 105, attack: 85 },
    image: "/assets/creation-v2/race-wyverian",
  },
  {
    id: "monster",
    label: "怪物",
    description:
      "待选择物种。该路线尚未开放，也不会以一套通用数值代替不同怪物的生态差异。",
    indices: null,
    locked: true,
    image: "/assets/creation-v2/race-monster-locked-v2.webp",
  },
];

export interface ProfessionOption {
  id: ProfessionId;
  label: string;
  description: string;
  hasHunterLicense: boolean;
}

export const PROFESSIONS: ProfessionOption[] = [
  {
    id: "hunter",
    label: "猎人",
    description: "持有公会资格，可承接任务、携带猎具并使用工房。",
    hasHunterLicense: true,
  },
  {
    id: "guild_staff",
    label: "公会职员",
    description: "负责委托、补给、档案或据点协调，熟悉公会制度。",
    hasHunterLicense: false,
  },
  {
    id: "investigator",
    label: "调查员",
    description: "追踪环境异变与怪物痕迹，以调查记录推动旅程。",
    hasHunterLicense: false,
  },
  {
    id: "artisan",
    label: "工匠",
    description: "依靠技术、素材知识和据点人脉生活的专业人员。",
    hasHunterLicense: false,
  },
  {
    id: "villager",
    label: "村民",
    description: "扎根当地的普通居民，与土地、习俗和邻里关系紧密。",
    hasHunterLicense: false,
  },
  {
    id: "researcher",
    label: "研究员",
    description: "研究生态、遗物或自然现象，倾向记录、推理与求证。",
    hasHunterLicense: false,
  },
  {
    id: "caravan",
    label: "商队人员",
    description: "随商路往来各地，擅长交涉、补给与远途见闻。",
    hasHunterLicense: false,
  },
  {
    id: "custom",
    label: "自定义",
    description: "自行填写身份；是否持有猎人资格由你决定。",
    hasHunterLicense: false,
  },
];

export interface BirthplaceOption {
  id: string;
  label: string;
  game: string;
  description: string;
  image: string;
}

const hubs = [
  [
    "kokoto",
    "科科特村",
    "怪物猎人",
    "群山环抱的古老猎人村落，也是许多猎人传说的起点。",
  ],
  [
    "jumbo",
    "江波村",
    "怪物猎人2",
    "面向海风与密林的新兴村庄，人们在开拓中共同维系生活。",
  ],
  [
    "pokke",
    "波凯村",
    "怪物猎人P2",
    "雪山脚下的温泉村，以古老矿脉、严寒和坚韧居民闻名。",
  ],
  [
    "yukumo",
    "结云村",
    "怪物猎人P3",
    "溪流与温泉环绕的和风村落，商旅与猎人常在此休整。",
  ],
  [
    "moga",
    "莫加村",
    "怪物猎人3",
    "建在海边的小渔村，生活与海洋生态和地震异变紧密相连。",
  ],
  [
    "tanzia",
    "坦吉亚港",
    "怪物猎人3G",
    "繁忙的海港据点，来自各地的猎人与货物在此汇集。",
  ],
  [
    "val-habar",
    "巴尔巴雷",
    "怪物猎人4",
    "随商队与集市流动而兴盛的沙海据点，旅途从车轮旁开始。",
  ],
  [
    "dondruma",
    "东多鲁玛",
    "怪物猎人2 / 4G",
    "由公会守护的大型城市，是贸易、狩猎与防卫的枢纽。",
  ],
  [
    "bherna",
    "贝尔纳村",
    "怪物猎人X",
    "高原上的牧羊村，与古代遗迹和龙历院调查队关系密切。",
  ],
  [
    "astera",
    "星辰",
    "怪物猎人：世界",
    "调查团在新大陆建成的据点，工房、研究与补给体系完备。",
  ],
  [
    "seliana",
    "月辰",
    "怪物猎人世界：冰原",
    "永霜冻土前线据点，以蒸汽设施抵御酷寒。",
  ],
  [
    "kamura",
    "神火村",
    "怪物猎人：崛起",
    "山林中的锻造之村，长期面对百龙夜行的威胁。",
  ],
  [
    "elgado",
    "埃尔迦德据点",
    "怪物猎人崛起：曙光",
    "王国设立的观测前线，骑士与研究人员共同应对异变。",
  ],
  [
    "windward-camp",
    "天堑沙原大本营",
    "怪物猎人：荒野",
    "调查队深入封禁之地后建立的沙原前线营地。",
  ],
] as const;

export const BIRTHPLACES: BirthplaceOption[] = hubs.map(
  ([id, label, game, description]) => ({
    id,
    label,
    game,
    description,
    image: `/assets/creation-v2/hub-${id}-v2.webp`,
  }),
);

export interface CreationInput {
  archiveName: string;
  characterName: string;
  gender: Exclude<CharacterGender, "unspecified">;
  raceId: Exclude<RaceId, "monster">;
  professionId: ProfessionId;
  customProfession?: string;
  customHasHunterLicense?: boolean;
  birthplaceId: string;
  background: string;
  hook: string;
}

const RACE_BACKGROUNDS: Record<CreationInput["raceId"], string> = {
  human:
    "你没有与生俱来的特殊优势，却习惯观察环境、学习他人的长处，并在陌生事务中寻找自己的做法。接下来结识什么人、走上哪条路，都要由你亲自决定。",
  felyne:
    "你带着艾露一族旺盛的精力、灵巧的双爪和对细微动静的敏锐，准备第一次独立面对据点里的工作与人情。你希望大家记住的不只是“一个能干的艾露”，还有你的名字和选择。",
  wyverian:
    "你从龙人一族的传统、旧闻与经验中学到了许多，但漫长的历史并不能替你决定这一段人生。你准备走出熟悉的教导，用亲身经历找到自己愿意坚持的道路。",
};

const RACE_HOOK_LEADS: Record<CreationInput["raceId"], string> = {
  human: "你整理好行装，准备亲手迈出新生活的第一步。",
  felyne: "你背好小挎包，抖抖耳朵，精神十足地准备迎接新生活。",
  wyverian: "你收好随身笔记，带着自己的判断准备迎接新生活。",
};

const HUNTER_HOOKS: Record<string, string> = {
  kokoto:
    "清晨，科科特村的老村长——那位昔日的“科科特英雄”——已经在集会所前等你。作为村里刚刚独立执业的猎人，你要先去向他报到；他准备从最基础的生肉采集开始，亲自教你怎样承接、完成并交付第一份村庄任务。你背好行囊，朝老村长走去。",
  jumbo:
    "江波村仍是一座正在成长的新村庄，你也是这里刚刚上任的猎人。村长正在村子中央等你，希望先带你认识居民，听听大家为了建设新生活提出的请求。第一份工作未必是讨伐大型怪物，却会决定你怎样成为这个村子真正的一员。你准备先去拜访村长。",
  pokke:
    "前往波凯村的途中，你遭到轰龙袭击并从雪山坠落；幸而一位退休猎人把你救回村中。如今你在陌生的住处醒来，借来的防具和新手装备已经放在身边。救命恩人提醒你：先去篝火旁拜见村长婆婆，再按照她的安排熟悉训练所和村庄任务。窗外积雪明亮，你的新生活就从寻找村长婆婆开始。",
  yukumo:
    "来结云村的路上，一辆丸鸟车捎了你一程；途中骤雨落下，你还远远看见了雷狼龙与笼罩山间的异样天色。抵达温泉村后，赶车的艾露与你道别，而年轻的龙人族村长正在等候公会派来的新猎人。你需要先向她报到，听听最近的怪物侵扰，也认识今后生活和工作的地方。",
  moga: "载着你的船刚刚靠近莫加村，脚下便传来一次明显的震动。村民短暂慌乱后重新忙碌起来，村长和他的儿子则在码头迎接公会派来的新猎人。村长希望你先去认识负责物资的村长之子，帮助村子恢复野外营地与基本补给，再逐步调查频繁地震的原因。海风迎面而来，你朝他们走去。",
  tanzia:
    "你带着刚登记的猎人资格来到坦吉亚港。这里没有村长替你安排单人旅程，港口酒馆、公会柜台和任务板就是猎人们汇合的起点。公会会长正在等待新登记的猎人，你需要先向他报到、确认自己的猎人等级，再由柜台职员介绍第一批集会任务与同行规则。",
  "val-habar":
    "前往巴尔巴雷的沙船遭到豪山龙袭击，你在混乱中协助守住甲板，直到公会王牌猎人赶来解围。安全抵达这座沙海集市后，那位一路观察你的团长主动找上门来：他正在组建首都商队，并邀请你成为商队的专属猎人。行会娘已经备好登记资料，等待你决定是否加入这支即将启程的队伍。",
  dondruma:
    "你刚被调入东多鲁玛的防卫猎人队伍，城墙、击龙枪和往来的商旅构成了全新的生活。王牌指挥官已经从公会收到你的登记资料，希望先当面确认你的能力，并介绍这座城市目前的防卫部署。远处高墙上的警戒旗正在风中展开，你的第一步是去指挥所向他报到。",
  bherna:
    "你刚受聘成为龙历院设在贝尔纳村的猎人。村长正在村口等候，准备带你认识龙历院首席研究员、任务柜台以及教授狩猎风格与狩技的教官。完成登记以后，贝尔纳看板娘就会把第一批调查委托交给你。你走向村长，准备开始在高原上的新生活。",
  astera:
    "第五期团的航船在新大陆近海遭遇熔山龙，你与接待员历经混乱才终于抵达星辰。新的房间、装备箱和调查据点都还十分陌生，总司令已经召集第五期团成员进行首次说明。接待员在通往指挥区的阶梯旁等你；完成报到后，你们将一起踏入古代树森林，开始第一项调查。",
  seliana:
    "你随调查队抵达永霜冻土，在地热与蒸汽之间第一次看见刚刚投入运转的月辰据点。设施仍在安置，前线成员也正适应严寒中的新生活。总司令准备说明冰原调查计划，接待员则已经整理好最初发现的冰鱼龙痕迹。你需要先完成报到，再决定从熟悉据点还是勘察周边开始。",
  kamura:
    "火芽和水芸一早把你从睡梦中叫醒，并带来公会正式认可你成为猎人的消息。村长普贤已经在村中等候，要亲自祝贺你取得资格，也要说明神火村正在面对的百龙夜行征兆。拜见村长后，倪泰教官还会带你完成第一次实地训练。你推开家门，熟悉的村庄从今天起有了新的意义。",
  elgado:
    "王国骑士菲奥莱娜带你乘船抵达埃尔迦德据点。这里的骑士、研究人员和工匠正在共同追查王国境内日益异常的怪物活动，而你将以新加入的猎人身份生活和工作。菲奥莱娜示意你先去指挥台拜见提督加雷亚斯；完成介绍后，任务柜台的琪切公主会为你登记第一项王国调查。",
  "windward-camp":
    "你作为禁地调查队的猎人进入天堑沙原，并在风沙与怪物群中完成了第一次紧急救援。回到大本营后，编纂者阿尔玛已经展开地图，纳塔也在等待调查队继续寻找他的故乡。你需要先认识营地里的调查成员、检查新的鹭鹰龙坐骑与补给，然后决定何时带队深入这片气候剧变的荒野。",
};

function professionBackground(
  professionId: ProfessionId,
  hub: string,
  customProfession?: string,
) {
  switch (professionId) {
    case "hunter":
      return "你的猎人资格才刚刚生效，武器、行囊和任务规程都还带着新手的生疏。成为猎人并不只意味着讨伐怪物；你还要学会判断风险、依靠伙伴，并为每一次出发负责。";
    case "guild_staff":
      return "你刚被安排到正式岗位，开始接触委托登记、补给交接和据点协调。柜台后的每一行记录都牵动着别人的旅程，而你正要学会怎样成为值得信赖的公会一员。";
    case "investigator":
      return "你刚领到自己的调查册，准备把课堂和前辈口中的知识带到真正的野外。痕迹、气候与怪物活动都不会主动给出答案，你要从第一次独立记录开始建立自己的判断。";
    case "artisan":
      return "你刚获得一张属于自己的工作台，工具尚未完全摆顺，认识你的客人也寥寥无几。你准备从第一件真正由自己负责的作品开始，让技术、素材知识和信誉一点点成为立足之本。";
    case "villager":
      return `你开始独立承担${hub}里的日常职责。耕作、采集、照料设施和帮助邻里看似平凡，却共同维系着据点的生活；你也将在这些熟悉的小事里找到自己的位置。`;
    case "researcher":
      return "你刚获准负责自己的第一项研究。书本里的结论只是起点，真正的生态、遗物与自然现象远比记录复杂；你准备凭观察、求证和耐心写下属于自己的发现。";
    case "caravan":
      return `你刚正式加入往来${hub}的商路。货物、补给和沿途消息都需要有人照看，而每一次启程也会把你带到新的面孔与风景之中；这是你第一次以商队成员的身份独立上路。`;
    case "custom":
      return `你终于能够以“${customProfession?.trim() || "自定义身份"}”的身份独立做事。前方没有现成的道路替你安排一切，你要从第一份工作、第一次相识和第一个决定开始，让这个身份真正成为自己的生活。`;
  }
}

function professionHook(
  professionId: ProfessionId,
  birthplaceId: string,
  hub: string,
  customProfession?: string,
) {
  switch (professionId) {
    case "hunter":
      return (
        HUNTER_HOOKS[birthplaceId] ??
        `公会柜台已经为你准备好新人说明和几份基础委托。你准备从认识${hub}里的伙伴、检查装备或挑选第一份任务之中，选择新生活的起点。`
      );
    case "guild_staff":
      return "前辈把今日的值班牌交到你手里：接待一名新到的猎人、核对一批补给，并熟悉任务柜台。三件事都不算危险，却会成为大家认识你的开始。你准备先做哪一件？";
    case "investigator":
      return "带队前辈在地图上标出了三处适合新人的观察地点：林边的新鲜足迹、水源附近的生态变化，以及昨夜留下的不明声响。你的第一份独立记录，将从选择调查方向开始。";
    case "artisan":
      return "工房为你留出了第一张工作台。桌上放着一件需要保养的旧猎具、一份等待鉴别的素材，以及一位想委托新人制作简单用品的客人。你准备先接下哪份工作？";
    case "villager":
      return "邻里带着早餐和笑声来欢迎你正式独立生活，也顺手带来了几件可以帮忙的小事：照看公共设施、陪同采集，或替忙碌的人送一份物品。你准备怎样度过自己的第一天？";
    case "researcher":
      return "研究室为你送来第一只资料箱，其中有尚未整理的观察记录、需要复核的旧结论，以及一份前往近郊采样的许可。你的第一项研究，将由你亲自选择从哪里开始。";
    case "caravan":
      return "商队将在中午启程。领队让你在清点货物、打听沿路消息和认识同行伙伴之间选择第一份职责；启程之前，你还有一个上午来熟悉这份新生活。";
    case "custom":
      return `一位将与你共事的人已经前来迎接，并询问你想怎样开始第一天：先熟悉环境、认识可能的伙伴，还是立刻尝试一件符合“${customProfession?.trim() || "自定义身份"}”的工作？`;
  }
}

export function defaultStory(
  input: Pick<
    CreationInput,
    "raceId" | "professionId" | "customProfession" | "birthplaceId"
  >,
) {
  const race = RACES.find((v) => v.id === input.raceId)?.label ?? "人类";
  const profession =
    input.professionId === "custom"
      ? input.customProfession?.trim() || "自定义身份"
      : (PROFESSIONS.find((v) => v.id === input.professionId)?.label ?? "猎人");
  const hub =
    BIRTHPLACES.find((v) => v.id === input.birthplaceId)?.label ?? "科科特村";
  return {
    background: `从今天起，你将在${hub}以${race}${profession}的身份，正式开始属于自己的生活。过去的日子里，你更多是在旁观、学习和接受照顾；如今准备已经结束，接下来要由你自己认识伙伴、承担工作，并为往后的旅程作出选择。${RACE_BACKGROUNDS[input.raceId]}${professionBackground(input.professionId, hub, input.customProfession)}`,
    hook: `${RACE_HOOK_LEADS[input.raceId]}${professionHook(input.professionId, input.birthplaceId, hub, input.customProfession)}`,
  };
}

export function profileFromCreation(input: CreationInput): CharacterProfile {
  const race = RACES.find((v) => v.id === input.raceId)!;
  const baseProfession = PROFESSIONS.find((v) => v.id === input.professionId)!;
  const birthplace = BIRTHPLACES.find((v) => v.id === input.birthplaceId)!;
  const profession =
    input.professionId === "custom"
      ? {
          ...baseProfession,
          label: input.customProfession?.trim() || "自定义身份",
          description: `自定义身份：${input.customProfession?.trim() || "未命名"}`,
          hasHunterLicense: Boolean(input.customHasHunterLicense),
        }
      : baseProfession;
  return {
    gender: input.gender,
    race: { id: input.raceId, label: race.label, indices: race.indices! },
    profession: { ...profession },
    birthplace: {
      id: birthplace.id,
      label: birthplace.label,
      description: birthplace.description,
    },
    background: input.background.trim(),
    hook: input.hook.trim(),
  };
}

export function hasHunterAccess(profile: CharacterProfile) {
  return profile.profession.hasHunterLicense;
}
